import React from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { CACHE_KEYS } from "@bn/api/apiv1";
import { useDeleteBooking } from "@api/v1/hooks/useBookings";
import { ConfirmDeleteDialog } from "@components";

export interface Props {
  bookingId: number;
  isOpen: boolean;
  onClose: () => void;
}

export const DeleteBookingDialog = ({ bookingId, isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const {
    mutate,
    isPending,
    isError,
    error,
    reset: mutationReset,
  } = useDeleteBooking({
    onSuccess: () => {
      handleClose();
      queryClient.invalidateQueries({
        queryKey: [CACHE_KEYS.LIST_AND_SEARCH_ALL_BOOKINGS],
      });
      queryClient.invalidateQueries({
        queryKey: [CACHE_KEYS.LIST_AND_SEARCH_ALL_INVESTMENTS],
      });
    },
    retry: false,
  });

  const handleClose = () => {
    onClose();
    mutationReset();
  };

  return (
    <ConfirmDeleteDialog
      isOpen={isOpen}
      onClose={handleClose}
      onDelete={() => mutate(bookingId)}
      isDeleteRequestLoading={isPending}
      title={t("investment.protocolTable.deleteDialog.title")}
      text={t("investment.protocolTable.deleteDialog.questionMessage")}
      confirmButtonText={t(
        "investment.protocolTable.deleteDialog.actions.delete"
      )}
      cancelButtonText={t(
        "investment.protocolTable.deleteDialog.actions.close"
      )}
      errorMessage={isError ? error?.response?.data?.message : undefined}
      testId="delete-booking-confirmation-dialog"
    />
  );
};
